import { useEffect, useRef, useState } from 'react';
import css from './index.module.scss';
import { useLang } from '@/i18n';
import EcoTab from 'eco-design/elements/eco-tab';
import EcoVideo from 'eco-design/base/eco-video';
import { useWebpOrJpg } from '../../hooks/useWebpOrJpg';
import getLocal from '@/app/utils/i18n';

const ListAni: React.FC = () => {
    const { locale } = getLocal();
    const { t } = useLang('dpux.ListAni');
    const { t: t2 } = useLang('dpux');

    const webpOrJpg = useWebpOrJpg('#delta-pro-ultra-x-ListAni');

    const list = [
        {
            tabValue: '1',
            tabLabel: t('item1_title'),
            pc: 'https://websiteoss.ecoflow.com/upload/video/1760095758922/01_Ready_1.mp4',
            subImg: 'https://websiteoss.ecoflow.com/upload/image/1760087720787/a.png',
            poster: 'https://websiteoss.ecoflow.com/upload/image/1760165121679/tab1-pc.jpg',
            desc: t.rich('item1_desc', {
                h: (v: string) => <span className="highlight">{v}</span>,
                sup: (v: string) => <sup>{v}</sup>,
            }),
        },
        {
            tabValue: '2',
            tabLabel: t('item2_title'),
            pc: 'https://websiteoss.ecoflow.com/upload/video/1760095759331/02_StromRaning_1.mp4',
            subImg: 'https://websiteoss.ecoflow.com/upload/image/1757919699700/2.png',
            poster: 'https://websiteoss.ecoflow.com/upload/image/1760165118123/tab2-pc.jpg',
            desc: t.rich('item2_desc', {
                h: (v: string) => <span className="highlight">{v}</span>,
                sup: (v: string) => <sup>{v}</sup>,
            }),
        },
        {
            tabValue: '3',
            tabLabel: t('item3_title'),
            pc: 'https://websiteoss.ecoflow.com/upload/video/1760095758784/03_Priority_1.mp4',
            subImg: '',
            poster: 'https://websiteoss.ecoflow.com/upload/image/1760165117112/tab3-pc.jpg',
            desc: t.rich('item3_desc', {
                h: (v: string) => <span className="highlight">{v}</span>,
                sup: (v: string) => <sup>{v}</sup>,
            }),
        },
    ];
    const [active, setActive] = useState(list[0].tabValue);
    const item = list.find((item) => item.tabValue === active) || list[0];

    /** 动画 */
    const sectionRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const aniRef = useRef<any>();
    const [needRadius, setNeedRadius] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.innerWidth < 1024) return;
        const aniFun = () => {
            if (!sectionRef.current || !videoRef.current || typeof window === 'undefined' || !bottomRef.current) return;
            aniRef.current?.destroy?.();

            let width, height;
            if (window.innerWidth >= 1680) {
                width = 1440;
                height = 640;
            } else if (window.innerWidth >= 1440) {
                width = window.innerWidth - 360;
                height = (480 / 1080) * width;
            } else if (window.innerWidth >= 1024) {
                width = window.innerWidth - 256;
                height = (342 / 768) * width;
            } else {
                return;
            }
            let top = (window.innerHeight - height - bottomRef.current.offsetHeight) / 2;
            if (window.innerWidth < 768) {
                top = (window.innerHeight - height - bottomRef.current.offsetHeight) / 2 + 30;
            }

            let x = -30;
            const subImgDom = document.querySelector('.delta-pro-ultra-x-ListAni .ListAni-videos .active .sub-img');
            if (subImgDom) {
                // 获取样式表里的 left
                const style = window.getComputedStyle(subImgDom);
                x = -parseFloat(style.left);
            }

            aniRef.current = AniContainer.create({
                triggerElement: sectionRef.current,
                triggerHook: 0,
                offset: '0%',
                duration: '220%',
                onProcess: (process: number) => {
                    setNeedRadius(process >= 1 / 2.2);
                },
                asyncFromTo: [
                    [`.delta-pro-ultra-x-ListAni .ListAni-videos`, { width: window.innerWidth, height: window.innerHeight, top: 0 }, { width, height, top }, 1],
                    [`.delta-pro-ultra-x-ListAni .ListAni-bottom`, { opacity: 0, y: top + height + 40 }, { opacity: 1, y: top + height }, 0.15],
                    [`.delta-pro-ultra-x-ListAni .ListAni-videos .active .sub-img`, { x, opacity: 0 }, { x: 0, opacity: 1 }, 0.15],
                    [`.delta-pro-ultra-x-ListAni .ListAni-videos`, { width, height, top }, { width, height, top }, 1.2],
                ],
                asyncTimeLine: [0, 0.9, 1, 1],
            });
        };
        aniFun();
        window.addEventListener('resize', aniFun);

        return () => {
            window.removeEventListener('resize', aniFun);
            aniRef.current?.destroy?.();
        };
    }, [active]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.innerWidth < 1024) return;
        const video = document.querySelector(`.delta-pro-ultra-x-ListAni .content .item-${active} .bg-video video`) as HTMLVideoElement;
        if (!video) return;
        video.play();

        return () => {
            video.pause();
            video.currentTime = 0;
        };
    }, [active]);

    /** M 逻辑 */
    const tabVideoRef = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        if (!tabVideoRef.current) return;
        if (typeof window === 'undefined') return;
        if (window.innerWidth >= 1024) return;
        const screen2Video = AniContainer.create({
            triggerElement: '.delta-pro-ultra-x-ListAni .ListAni-videos',
            triggerHook: 1,
            offset: '50%',
            duration: '100%',
            onProcess: (process: number) => {
                const video = document.querySelector(`.delta-pro-ultra-x-ListAni .device-m-content .ListAni-videos  video`) as HTMLVideoElement;
                if (process <= 0) {
                    video.currentTime = 0;
                    video.pause();
                }
            },
            onEnter: () => {
                const video = document.querySelector(`.delta-pro-ultra-x-ListAni .device-m-content .ListAni-videos  video`) as HTMLVideoElement;
                video?.play?.();
            },
        });

        const callback = () => {
            if (!tabVideoRef.current) return;
            tabVideoRef.current.play();
        };
        tabVideoRef.current.addEventListener('canplay', callback);
        return () => {
            screen2Video?.destroy();
            tabVideoRef?.current?.removeEventListener('canplay', callback);
        };
    }, []);

    return (
        <section
            className={`${css.section} delta-pro-ultra-x-ListAni GtmModuleData-section`}
            id="delta-pro-ultra-x-ListAni"
            data-section-id={'delta-pro-ultra-x-ListAni'}
            data-section-title={t('title')}
            ref={sectionRef}
        >
            <div className="content pc">
                <div className={`ListAni-videos ${needRadius ? 'need-radius' : ''}`} ref={videoRef}>
                    {list.map((i, idx) => (
                        <div className={`media item-${i.tabValue} ${active === i.tabValue ? 'active' : ''}`} key={idx}>
                            <EcoVideo className="bg-video" loop={true} autoPlay={active === i.tabValue} pc={i.pc} poster={i.poster} />
                            <img className={`sub-img ${item.subImg ? '' : 'hide'}`} src={webpOrJpg(i.subImg)} loading="lazy" draggable="false" />
                        </div>
                    ))}
                </div>
                <div className="ListAni-bottom" ref={bottomRef}>
                    <EcoTab items={list} theme="black" onChange={(i) => setActive(i.tabValue)} />
                    <div className="info">
                        <div className="info-desc">{list.find((i) => i.tabValue === active)?.desc}</div>
                        {locale === 'ca' && <div className="footer-tips mt">{t2('item_Remarks3')}</div>}
                    </div>
                </div>
            </div>

            <div className="device-m-content">
                <div className="ListAni-videos">
                    <div className={`media`}>
                        <video className="bg-video" src={item.pc} playsInline loop ref={tabVideoRef} />
                        <img className={`sub-img ${item.subImg ? '' : 'hide'}`} src={item.subImg} loading="lazy" draggable="false" />
                    </div>
                </div>
                <div className="ListAni-bottom">
                    <EcoTab
                        items={list}
                        theme="black"
                        onChange={(i) => {
                            setActive(i.tabValue);
                        }}
                    />
                    <div className="info">
                        <div className="info-desc">{list.find((i) => i.tabValue === active)?.desc}</div>
                        {locale === 'ca' && <div className="footer-tips mt">{t2('item_Remarks3')}</div>}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ListAni;
